import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin } from 'react-icons/fa';
import axiosInstance from '../../utils/axiosInstance';

// --- THEME PALETTE (Matches Navbar) ---
const COLORS = {
  bg: "#020617",         // Rich Black
  panel: "#0f172a",      // Slate-900 (Panel BG)
  accent: "#22d3ee",     // Cyan-400 (The Glow)
  textMain: "#cbd5e1",   // Slate-300
  textMuted: "#64748b",  // Slate-500
};

const Footer = () => {
  const [categories, setCategories] = useState([]);

  // 1. Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axiosInstance.get('/categories');
        setCategories(data.sort((a, b) => a.sequence - b.sequence));
      } catch (error) {
        console.error("Failed to fetch categories for footer:", error);
      }
    };
    fetchCategories();
  }, []);

  const quickLinkCategories = categories.slice(0, 4);
  const importantLinkCategories = categories.slice(4, 9);

  return (
    <footer className="relative bg-[#020617] text-slate-300 pt-20 pb-10 font-sans overflow-hidden border-t border-[#22d3ee]/20">
      
      {/* --- DECORATIVE BACKGROUND ELEMENTS --- */}
      {/* Top glowing line (Scanner effect) */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent opacity-50"></div>
      
      {/* Subtle Radial Glow in corner */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#22d3ee]/5 blur-[100px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          
          {/* 1. Brand Section */}
          <div className="md:col-span-1 space-y-6">
            {/* Geometric Logo (Matching Navbar) */}
            <Link to="/" className="flex items-center gap-3 group w-fit">
               <div className="w-8 h-8 border border-[#22d3ee] rotate-45 flex items-center justify-center group-hover:bg-[#22d3ee]/10 transition-all duration-500 group-hover:rotate-180 shadow-[0_0_10px_rgba(34,211,238,0.3)]">
                  <div className="w-4 h-4 bg-[#22d3ee] shadow-[0_0_10px_#22d3ee]"></div>
               </div>
               <span className="font-sans text-xl font-bold tracking-[0.2em] text-white group-hover:text-[#22d3ee] transition-colors duration-300">
                 WEB<span className="text-[#22d3ee]">THEME</span>
               </span>
            </Link>

            <p className="text-sm text-slate-500 leading-relaxed font-mono">
              {/* // SYSTEM_MSG:<br/> */}
              {/* Accessing global style database. Initiating luxury protocols and trend analysis streams. */}
              Best place to get premium and free templates.
            </p>
          </div>

          {/* 2. Quick Links */}
          <div>
            <h4 className="font-mono text-xs font-bold text-[#22d3ee] tracking-widest mb-6 uppercase flex items-center gap-2">
              <span className="w-2 h-2 bg-[#22d3ee] rounded-full animate-pulse"></span>
              Navigation
            </h4>
            <ul className="space-y-3">
              <li><FooterLink to="/" text="Home" /></li>
              <li><FooterLink to="/products" text="Products" /></li>
              {quickLinkCategories.map(cat => (
                <li key={cat.id}>
                  <FooterLink to={`/products?category=${cat.id}`} text={cat.name} />
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Important Links */}
          <div>
            <h4 className="font-mono text-xs font-bold text-[#22d3ee] tracking-widest mb-6 uppercase flex items-center gap-2">
              <span className="w-2 h-2 bg-[#22d3ee] rounded-full animate-pulse delay-75"></span>
              Modules
            </h4>
            <ul className="space-y-3">
              {importantLinkCategories.map(cat => (
                <li key={cat.id}>
                  <FooterLink to={`/products?category=${cat.id}`} text={cat.name} />
                </li>
              ))}
              <li><FooterLink to="/about" text="About System" /></li>
              <li><FooterLink to="/contact" text="Contact Us" /></li>
            </ul>
          </div>

          {/* 4. Social Media */}
          <div>
            <h4 className="font-mono text-xs font-bold text-[#22d3ee] tracking-widest mb-6 uppercase flex items-center gap-2">
              <span className="w-2 h-2 bg-[#22d3ee] rounded-full animate-pulse delay-150"></span>
              Network
            </h4>
            <div className="flex gap-4">
              {/* <SocialLink href="https://facebook.com" icon={<FaFacebook size={18} />} />
              <SocialLink href="https://twitter.com" icon={<FaTwitter size={18} />} />
              <SocialLink href="https://instagram.com" icon={<FaInstagram size={18} />} />
              <SocialLink href="https://linkedin.com" icon={<FaLinkedin size={18} />} /> */}
            </div>
            <p className="mt-6 text-[10px] text-slate-600 font-mono">
              ESTABLISHED CONNECTION SECURE.
            </p>
          </div>
        </div>

        {/* Copyright / Status Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-xs font-mono text-slate-600">
          <p>&copy; {new Date().getFullYear()} WEBTHEME. ALL RIGHTS RESERVED.</p>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
             <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                SYSTEM ONLINE
             </span>
             <span>V.2.0.45</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper: Futuristic Link with Slide Effect
const FooterLink = ({ to, text }) => (
  <Link 
    to={to} 
    className="group flex items-center text-slate-400 hover:text-[#22d3ee] transition-all duration-300 hover:translate-x-2"
  >
    <span className="w-0 h-[2px] bg-[#22d3ee] mr-0 group-hover:w-3 group-hover:mr-2 transition-all duration-300"></span>
    <span className="text-sm font-medium tracking-wide">{text}</span>
  </Link>
);

// Helper: Hexagon/Square Tech Social Buttons
const SocialLink = ({ href, icon }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="bg-[#0f172a] border border-[#1e293b] p-3 text-slate-400 hover:text-[#22d3ee] hover:border-[#22d3ee] hover:shadow-[0_0_15px_rgba(34,211,238,0.4)] transition-all duration-300 group relative overflow-hidden"
  >
    <div className="relative z-10">{icon}</div>
    {/* Hover fill effect */}
    <div className="absolute inset-0 bg-[#22d3ee]/10 transform scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full"></div>
  </a>
);

export default Footer;